import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Cookies } from 'react-cookie';
import { CHANNEL_ID, CHANNEL_ID_TYPES } from '../constants/common';
import { GoalsFilter } from '../pages/explore/axisSelectFunds';
import { KycDetails } from '../pages/kyc';
import { CartItem } from '../components/modules/cart/CartPage';

import { ReturnsFilter } from '../pages/explore/fundsWithModerateToHighReturns';

interface SchemeListingState {
  pageNumber: number;
  subCat: string;
  nfoCat: string;
  showAxisSelectOnly: boolean;
  duration: string;
}

interface InsightPageState {
  showAxisSelectOnlyInSchemeList: boolean | null;
  selectedAmc: string | null;
}

interface ContextState {
  kyc: KycDetails | null;
  successRoute: string;
  riskProfile: string | null;
  isRiskProfileExpired: boolean | null;
  schemeListingState: SchemeListingState | null;
  axisSelectActiveGoal: GoalsFilter | null;
  customerId: string | null;
  inAppBackPressed: boolean;
  insightPage: InsightPageState | null;
  showHeader: boolean;
  pendingTransaction: boolean;
  informationCheck: string;
  liquidCutOffTiming: string;
  otherCutOffTiming: string;
  liquidSwitch: string;
  encryptedInvestmentIds: object;
  cartData: Array<CartItem> | null;
  cartSchemeCount: Number;
  currentInvestmentId: string;
  channelId: string;
  isOverlayBannerDisplayed: boolean;
  subCategory: string;
  isCorporate: boolean;
  profileName?: string;
  minorCustId?: string | null;
  solePropId?: string;
  kycStatus?: string;
  invIdLevelCheck?: boolean;
  fatcaCompleted?: boolean;
  riskProfileExpired?: boolean;
  customerAddress?: string;
}

export const initialState: ContextState = {
  kyc: null,
  successRoute: '',
  riskProfile: null,
  isRiskProfileExpired: null,
  schemeListingState: null,
  axisSelectActiveGoal: { key: '', displayName: '' },
  customerId: null,
  inAppBackPressed: false, // used to browser back button restriction. see common/BackButton
  insightPage: null,
  showHeader: true, // Header won't be shown in the whole app for channelId MBK
  pendingTransaction: false, // For newly registered customers whose folio has not been allotted yet it will be true
  informationCheck: '',
  liquidCutOffTiming: '',
  otherCutOffTiming: '',
  liquidSwitch: '',
  encryptedInvestmentIds: {},
  cartData: [],
  cartSchemeCount: null,
  currentInvestmentId: '',
  channelId: '',
  isOverlayBannerDisplayed: false,
  subCategory: '',
  isCorporate: false,
  profileName: '',
  minorCustId: null,
  solePropId: undefined,
  kycStatus: undefined,
  invIdLevelCheck: false,
  riskProfileExpired: undefined,
  fatcaCompleted: undefined,
  customerAddress: undefined,
};

const AppContext = createContext({
  state: initialState,
  setState: (initialState: ContextState) => {},
  resetState: () => {},
});

export function AppWrapper({ children, customerId, showHeader, isCorporate }) {
  const [state, setState] = useState<ContextState>(initialState);
  const cookies = new Cookies();

  useEffect(() => {
    let temp = { ...state };
    // If customerId is available then set it
    if (customerId) {
      temp.customerId = customerId;
    }
    if (cookies.get(CHANNEL_ID)) {
      temp.channelId = cookies.get(CHANNEL_ID);
    }

    // If channelID is changed in cookies available then set accordingly hide/show header
    if (cookies.get(CHANNEL_ID)) {
      temp.showHeader = cookies.get(CHANNEL_ID) !== CHANNEL_ID_TYPES.MBK;
    }

    if (isCorporate) {
      temp.isCorporate = isCorporate;
    }

    setState({ ...state, ...temp });
  }, [cookies.get(CHANNEL_ID), customerId, isCorporate]);

  const resetState = () => {
    setState(initialState);
  };

  return (
    <AppContext.Provider value={useMemo(() => ({ state, setState, resetState }), [state, setState])}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  return useContext(AppContext);
}
